// assignment-chrome — the student assignment editor surface host chrome
// (clientside-teacher-review, task 4.3). Wires debounced autosave-to-Draft
// (editor-api.saveDraft) on edits and Submit (editor-api.submit with the
// course/lesson/material context), reflecting submission state on success. Reuses the
// shared Assets panel when uploads are enabled (template-gated).
//
//   wire(ctx) — ctx = { core, payload, trinketId, clientsideMode, isOwner, editorApi, doc }
// editor-core is already constructed (editable) and loaded by main.js.

import { wireAssets } from './assets-panel.js';
import { wire as wireAuthenticityCapture } from './authenticity-capture.js';
import { wireSuccessCriteriaDrawer } from './success-criteria-drawer.js';
import { initSuggestions } from './suggestions-panel.js';
import { mountPreviewStrip, disableForPreview } from './preview-strip.js';
import { registerSaveHandler } from './editor-shortcuts.js';
import { readScheduleLock, disableForLock } from './schedule-lock.js';
import { offerClose } from './close-prompt.js';

var AUTOSAVE_DEBOUNCE_MS = 1500;

// student-versions (Trello ch3IfCAK): the wired page's flush, or null before `wire` runs.
var activeFlush = null;

// Cancel the private autosave debounce and save the live files NOW. Resolves the draft
// endpoint's reply (`{ success: true }` when it saved — `project.draft` swallows errors into a
// 200, so a caller that needs the write checks `success`), rejects on a network/HTTP failure.
// Resolves `null` when no assignment surface is wired. `versions-chrome.js` awaits it before a
// Save version or a Restore so the server reads the student's latest code.
export function flushPendingSave() {
  return activeFlush ? activeFlush() : Promise.resolve(null);
}

// one-tab-topbar (Trello 3h9FqNWJ): EXPLICIT save state for the activity top bar's Back, so Back
// flushes ONLY real unsaved work and never writes a Draft on a page with nothing to save (the
// Draft is the student's source of truth). Never "the timer is truthy".
//   pending    — the autosave debounce is scheduled (cleared when it fires or a flush cancels it)
//   inflight   — save requests outstanding (a count: a Ctrl-S can overlap a debounce save)
//   lastFailed — the last save to finish rejected or resolved `success !== true`; cleared by the
//                next successful save
// `hasPendingSave()` is false when the surface is not wired (a plan-locked page) and on a
// schedule-locked (read-only) page.
var saveState = { wired: false, readOnly: false, pending: false, inflight: 0, lastFailed: false };

export function hasPendingSave() {
  if (!saveState.wired || saveState.readOnly) { return false; }
  return saveState.pending || saveState.inflight > 0 || saveState.lastFailed;
}

// one-tab-topbar: the in-flight Submit POST (or null). Back waits for it to settle before it
// flushes and navigates. Resolves (never rejects) at once when no Submit is in flight.
var activeSubmit = null;

export function whenSubmitSettled() {
  if (!activeSubmit) { return Promise.resolve(); }
  return activeSubmit.then(function () {}, function () {});
}

// one-tab-topbar: the assignment's ONE ending — a successful Submit offers the same-tab
// countdown. One-shot per page load, spent only when `offerClose` is actually called (a submit
// while the program is running spends nothing; a re-submit after a cancelled countdown offers
// nothing, because `offerClose` itself is one-shot — accepted, decisions.md).
var endingSpent = false;

// one-tab-topbar: the runner's last state, recorded the way `guided-make-chrome.js` records its
// own (`core.on('state', …)`). EXPLICIT predicate: `null` = never run = NOT running — never an
// inversion of guided-make's `programHasStopped()`, which is true-for-null.
var lastRunnerState = null;
function programIsRunning() {
  return lastRunnerState === 'booting' || lastRunnerState === 'running';
}

// Wrap one Draft save so the explicit state follows it. Returns the save's own promise.
function trackSave(promise) {
  saveState.inflight += 1;
  return Promise.resolve(promise).then(function (res) {
    saveState.inflight = Math.max(0, saveState.inflight - 1);
    saveState.lastFailed = !(res && res.success === true);
    return res;
  }, function (err) {
    saveState.inflight = Math.max(0, saveState.inflight - 1);
    saveState.lastFailed = true;
    throw err;
  });
}

/** Test-only reset so module state does not leak between specs. */
export function __resetAssignmentChrome() {
  activeFlush = null;
  activeSubmit = null;
  endingSpent = false;
  lastRunnerState = null;
  saveState = { wired: false, readOnly: false, pending: false, inflight: 0, lastFailed: false };
}

export function wire(ctx) {
  var document = ctx.doc;
  var core = ctx.core;
  var api = ctx.editorApi;
  var body = document.body;

  // The Draft endpoint keys by the trinket's _id (Draft.trinket is an ObjectId ref);
  // the surface renders it as data-draft-project-id (data-project-id stays the shortCode
  // for the asset proxy). Submit's `parent` accepts either (findById resolves both).
  var draftTrinketId = body.getAttribute('data-draft-project-id') ||
    body.getAttribute('data-project-id') || '';
  var courseCtx = {
    courseId: body.getAttribute('data-course-id') || '',
    lessonId: body.getAttribute('data-lesson-id') || '',
    materialId: body.getAttribute('data-material-id') || ''
  };

  // Teacher code suggestions: render any PENDING suggestion as a side-by-side diff with
  // Accept/Dismiss (rebuild of the phase-3 UI dropped in the editor-surface migration).
  initSuggestions({ projectId: draftTrinketId, doc: document });

  var saveStatus = document.getElementById('assignment-save-status');
  var submitBtn = document.getElementById('assignment-submit');

  // teacher-preview-split (tasks 9.4/9.5 — R24/R10): this surface is project-keyed and
  // owner-gated by `helpers.requireProjectCreator`, so its preview state comes from
  // `Snippet.isPreview` on the working copy (threaded through `assignmentSurfaceData` into
  // the template), NOT from a URL flag — which is also why it can never be forged, and why
  // "Leave preview" navigates BACK to the classroom rather than reloading without a flag.
  //
  // teacher-preview-cleanup (finding 2): the exit NAVIGATES to the classroom URL the classroom
  // itself supplied (`data-return-url`, preview flag already stripped). `history.back()` went to
  // that URL still carrying `?preview=student`, so it re-entered preview. With no valid target
  // the strip renders with NO exit button — reloading without a query flag cannot leave a mode
  // whose flag is `Snippet.isPreview`, so a control there would be dead.
  var previewMode = body.getAttribute('data-preview') === 'true';
  // one-tab-topbar (Trello 3h9FqNWJ): where the activity top bar is rendered, ITS Back and
  // preview chip replace the strip (preview Back keeps the strip's `location.replace` exit). The
  // guard keys on the bar's markup only, so a preview render without the bar keeps the strip.
  if (!document.getElementById('surface-topbar-back')) {
    mountPreviewStrip(document, {
      active: previewMode,
      exit: 'navigate',
      exitUrl: body.getAttribute('data-return-url') || ''
    });
  }

  function setStatus(text, isError) {
    if (!saveStatus) { return; }
    saveStatus.textContent = text || '';
    saveStatus.classList.toggle('runner-error', !!isError);
  }

  // Debounced autosave-to-Draft. editor-core already loaded the initial content before
  // wire() runs, so the change listener only fires on the student's own edits. getFiles()
  // carries every tab so siblings are never dropped.
  var timer = null;
  function scheduleSave() {
    if (timer) { clearTimeout(timer); }
    setStatus('Editing…', false);
    saveState.pending = true;
    timer = setTimeout(function () {
      timer = null;
      saveState.pending = false;
      setStatus('Saving…', false);
      trackSave(api.saveDraft(draftTrinketId, core.getFiles()))
        .then(function () { setStatus('Saved', false); })
        .catch(function (err) { setStatus('Save failed', true); console.error(err); });
    }, AUTOSAVE_DEBOUNCE_MS);
  }
  var editor = core.getEditor();
  if (editor && editor.on) {
    editor.on('change', scheduleSave);
  }

  // class-due-dates: past the class due date with the lock on, a real student's page loads
  // READ-ONLY (client-side only — the server refuses the submit itself). The server-rendered
  // banner carries the message. A teacher / course-staff session sees the same banner but
  // keeps a live editor and Submit.
  var scheduleLock = readScheduleLock(document);
  if (scheduleLock.enforce && editor && editor.setReadOnly) {
    editor.setReadOnly(true);
  }
  // one-tab-topbar: the save state is live from here; a read-only page never reports pending work.
  saveState.wired = true;
  saveState.readOnly = scheduleLock.enforce === true;

  // Ctrl-S / Cmd-S flushes the pending autosave immediately instead of waiting out the
  // debounce, and saves unconditionally — reporting `Saved` even when nothing was pending,
  // because a keypress that produces no visible change is the complaint the card raises.
  // Cancelling the armed timer is required: otherwise it fires ~1.5s later, producing a
  // second POST that can land out of order and overwrite the status this just wrote.
  // Draft only — Ctrl-S never submits. This surface has no button to disable, hence the
  // explicit in-flight flag. Preview mode registers normally: it already autosaves to the
  // throwaway preview copy and already writes `Saved` two rows below the strip.
  var saving = false;
  registerSaveHandler(function () {
    if (saving) { return; }
    if (timer) { clearTimeout(timer); timer = null; }
    saveState.pending = false;
    saving = true;
    setStatus('Saving…', false);
    trackSave(api.saveDraft(draftTrinketId, core.getFiles()))
      .then(function () { setStatus('Saved', false); })
      .catch(function (err) { setStatus('Save failed', true); console.error(err); })
      .then(function () { saving = false; });
  });

  // student-versions: the exported flush (see `flushPendingSave` above).
  activeFlush = function () {
    if (timer) { clearTimeout(timer); timer = null; }
    saveState.pending = false;
    setStatus('Saving…', false);
    return trackSave(api.saveDraft(draftTrinketId, core.getFiles()))
      .then(function (res) {
        setStatus(res && res.success === true ? 'Saved' : 'Save failed', !(res && res.success === true));
        return res;
      },
        function (err) { setStatus('Save failed', true); throw err; });
  };

  // one-tab-topbar: record the runner's state (guided-make-chrome.js ~943's pattern) BEFORE
  // Submit is wired, so the submitted signal can say whether the program is running.
  if (core && typeof core.on === 'function') {
    core.on('state', function (state) { lastRunnerState = state; });
  }

  // one-tab-topbar: the ending, in ONE place. Fires only on the success signal below (never
  // after a failed submit, and never when the test-table guard or the versions nudge stopped the
  // click — they stop it before Submit's own listener runs), never in preview, and never while
  // the program is running (the countdown must not take a page the student is using; plan-chrome
  // still offers its revise prompt then). `offerClose` consults plan-chrome's deferral first.
  document.addEventListener('pc-assignment-submitted', function (e) {
    if (previewMode || endingSpent) { return; }
    if (e && e.detail && e.detail.running === true) { return; }
    endingSpent = true;
    offerClose({ sameTab: true, label: 'Nice work — you handed it in.' });
  });

  // Submit: snapshot the live files via the existing submission endpoint.
  if (previewMode) {
    // The explanation itself is the server-rendered #assignment-preview-note beside the button
    // (finding 3 / A20) — this only disables the control and mirrors the reason into `title`.
    disableForPreview(submitBtn, 'Submitting is turned off in preview — nothing is saved.');
  }
  if (scheduleLock.enforce) {
    disableForLock(document, submitBtn);
  }
  if (submitBtn && !previewMode && !scheduleLock.enforce) {
    submitBtn.addEventListener('click', function () {
      submitBtn.disabled = true;
      setStatus('Submitting…', false);
      var submitting = Promise.resolve(api.submit(courseCtx, draftTrinketId, core.getFiles(), {}));
      // one-tab-topbar: Back waits on this (`whenSubmitSettled`).
      activeSubmit = submitting;
      submitting.then(function () { activeSubmit = null; }, function () { activeSubmit = null; });
      submitting
        .then(function (res) {
          var state = (res && res.submission && res.submission.submissionState) || 'submitted';
          setStatus(state === 'submittedLate' ? 'Submitted (late)' : 'Submitted', false);
          // plan-first-activity: the success signal `plan-chrome.js` listens for (its revise-
          // after-pass prompt on an assignment host); one-tab-topbar: this module's own ending
          // listens for it too. `running` = the program was running at this moment.
          try {
            document.dispatchEvent(new CustomEvent('pc-assignment-submitted', {
              detail: { state: state, running: programIsRunning() }
            }));
          }
          catch (e) { /* a signal only — never break the submit path */ }
        })
        .catch(function (err) {
          setStatus('Submit failed', true);
          submitBtn.disabled = false;
          console.error(err);
        });
    });
  }

  // assignment-success-criteria ([4.3]): the "Success criteria" self-tick drawer.
  // Reads the teacher-authored criteria + the student's saved ticks from the page
  // payload (whitelisted by editor-api.readLoadPayload). Only reveals when the
  // assignment carries criteria; self-ticks persist on THIS attempt's Snippet via
  // the dedicated assignmentChecklist route and NEVER gate grading/submission.
  var payload = ctx.payload || {};
  wireSuccessCriteriaDrawer({
    doc: document,
    criteria: payload.successCriteria,
    ticks: payload.checklistTicks,
    saveTicks: function (indices) {
      return api.saveAssignmentChecklistTicks(courseCtx, draftTrinketId, indices);
    }
  });

  // Optional Assets panel (when features.assets + the template renders the controls).
  wireAssets(ctx);

  // Code-authenticity paste capture (code-authenticity-snapshots, task 4). Inert unless
  // the assignment body carries data-authenticity="true" (server-injected when the
  // operator flag is on). Records flagged pastes to the working trinket.
  wireAuthenticityCapture({ core: core, api: api, doc: document, draftTrinketId: draftTrinketId });
}
